import React, { useState } from 'react';
import BaseModal from './common/BaseModal';
import SearchProgressLogs from './common/SearchProgressLogs';
import HashtagAnalysisModal from './HashtagAnalysisModal';

// Helper function for Full Hashtag Search
const getTopPostsPerCreator = (posts: any[]) => {
  if (!Array.isArray(posts)) return [];

  // Group posts by creator
  const creatorPosts = new Map<string, any[]>();

  posts.forEach(post => {
    const creatorId = post.authorInfos?.uniqueId || post.authorMeta?.name || post.author?.unique_id || '';
    if (creatorId) {
      if (!creatorPosts.has(creatorId)) {
        creatorPosts.set(creatorId, []);
      }
      creatorPosts.get(creatorId)!.push(post);
    }
  });

  // Get the highest view count post for each creator
  const topPosts: any[] = [];
  creatorPosts.forEach((posts, creatorId) => {
    const topPost = posts.reduce((best, current) => {
      const bestViews = best.itemInfos?.playCount || best.videoMeta?.playCount || best.playCount || best.statistics?.play_count || 0;
      const currentViews = current.itemInfos?.playCount || current.videoMeta?.playCount || current.playCount || current.statistics?.play_count || 0;
      return currentViews > bestViews ? current : best;
    });
    topPosts.push(topPost);
  });

  // Sort by views descending
  topPosts.sort((a, b) => {
    const aViews = a.itemInfos?.playCount || a.videoMeta?.playCount || a.playCount || a.statistics?.play_count || 0;
    const bViews = b.itemInfos?.playCount || b.videoMeta?.playCount || b.playCount || b.statistics?.play_count || 0;
    return bViews - aViews;
  });

  return topPosts;
};

interface TikTokCreatorsSearchModalProps {
  showTikTokModal: boolean;
  setShowTikTokModal: (show: boolean) => void;
  tiktokSearchResult: any;
  setTiktokSearchResult: (result: any) => void;
  selectedPosts: Set<string>;
  setSelectedPosts: (posts: Set<string>) => void;
  searchLogs: string[];
  clearSearchLogs: () => void;
  showSearchLogs: boolean;
  setShowSearchLogs: (show: boolean) => void;
  tiktokSearchKeyword: string;
  setTiktokSearchKeyword: (keyword: string) => void;
  tiktokSearchType: 'keyword' | 'hashtag' | 'full-hashtag' | 'full-keyword';
  setTiktokSearchType: (type: 'keyword' | 'hashtag' | 'full-hashtag' | 'full-keyword') => void;
  tiktokSearchParams: any;
  setTiktokSearchParams: React.Dispatch<React.SetStateAction<any>>;
  tiktokMinViews: number;
  setTiktokMinViews: (views: number) => void;
  tiktokSearching: boolean;
  searchTikTokCreators: () => void;
  cancelCurrentSearch: () => void;
  tiktokSortBy: 'views' | 'likes' | 'comments' | 'date';
  setTiktokSortBy: React.Dispatch<React.SetStateAction<'views' | 'likes' | 'comments' | 'date'>>;
  tiktokMinLikes: number;
  setTiktokMinLikes: (likes: number) => void;
  tiktokMinComments: number;
  setTiktokMinComments: (comments: number) => void;
  sortAndFilterTikTokResults: (data: any[]) => any[];
  addedCreators: Set<string>;
  existingCreators: Set<string>;
  toggleSelectAllPosts: () => void;
  togglePostSelection: (postUrl: string) => void;
  addingCreators: Set<string>;
  addTikTokCreator: (post: any) => void;
  fetchTranscript: (url: string) => void;
  addSelectedPosts: () => void;
  bulkAddingPosts: boolean;
}

// Add keyframes for spin animation
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TikTokCreatorsSearchModal: React.FC<TikTokCreatorsSearchModalProps> = ({
  showTikTokModal,
  setShowTikTokModal,
  tiktokSearchResult,
  setTiktokSearchResult,
  selectedPosts,
  setSelectedPosts,
  searchLogs,
  clearSearchLogs,
  showSearchLogs,
  setShowSearchLogs,
  tiktokSearchKeyword,
  setTiktokSearchKeyword,
  tiktokSearchType,
  setTiktokSearchType,
  tiktokSearchParams,
  setTiktokSearchParams,
  tiktokMinViews,
  setTiktokMinViews,
  tiktokSearching,
  searchTikTokCreators,
  cancelCurrentSearch,
  tiktokSortBy,
  setTiktokSortBy,
  tiktokMinLikes,
  setTiktokMinLikes,
  tiktokMinComments,
  setTiktokMinComments,
  sortAndFilterTikTokResults,
  addedCreators,
  existingCreators,
  toggleSelectAllPosts,
  togglePostSelection,
  addingCreators,
  addTikTokCreator,
  fetchTranscript,
  addSelectedPosts,
  bulkAddingPosts
}) => {
  const [showHashtagModal, setShowHashtagModal] = useState(false);

  const closeModal = () => {
    cancelCurrentSearch();
    setShowTikTokModal(false);
    setTiktokSearchResult(null);
    setSelectedPosts(new Set());
    clearSearchLogs();
    setShowSearchLogs(false);
  };

  // Determine modal title and subtitle
  const getModalTitle = () => {
    if (tiktokSearchResult) {
      // For full-hashtag search, don't show count in title
      if (tiktokSearchType === 'full-hashtag') {
        return 'TikTok Search Results';
      }
      const filtered = sortAndFilterTikTokResults(tiktokSearchResult.data || []);
      return `TikTok Search Results (${filtered.length} posts)`;
    }
    return 'TikTok Creators Search';
  };

  const getModalSubtitle = () => {
    if (tiktokSearchResult) {
      return `Keyword: ${tiktokSearchKeyword || 'N/A'}`;
    }
    return 'Find TikTok creators by keyword search';
  };

  return (
    <>
      <style>{spinKeyframes}</style>
      <BaseModal
      show={showTikTokModal}
      onClose={closeModal}
      title={getModalTitle()}
      subtitle={getModalSubtitle()}
      size="xlarge"
      headerGradient={false}
      showHeader={true}
    >
      <div style={{ padding: '32px' }}>
        {!tiktokSearchResult ? (
          /* Search UI - Before Search */
          <>
            {/* Search Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Search Type</label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="keyword"
                    checked={tiktokSearchType === 'keyword'}
                    onChange={(e) => setTiktokSearchType(e.target.value as 'keyword')}
                    disabled={tiktokSearching}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium">Keyword Search</div>
                    <div className="text-xs text-gray-500">Search for posts containing keywords</div>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="hashtag"
                    checked={tiktokSearchType === 'hashtag'}
                    onChange={(e) => setTiktokSearchType(e.target.value as 'hashtag')}
                    disabled={tiktokSearching}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium">Hashtag Search</div>
                    <div className="text-xs text-gray-500">Search for posts with specific hashtag</div>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="full-keyword"
                    checked={tiktokSearchType === 'full-keyword'}
                    onChange={(e) => setTiktokSearchType(e.target.value as 'full-keyword')}
                    disabled={tiktokSearching}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium">Full Keyword Search</div>
                    <div className="text-xs text-gray-500">Get all posts with keyword (auto-pagination)</div>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="full-hashtag"
                    checked={tiktokSearchType === 'full-hashtag'}
                    onChange={(e) => setTiktokSearchType(e.target.value as 'full-hashtag')}
                    disabled={tiktokSearching}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium">Full Hashtag Search</div>
                    <div className="text-xs text-gray-500">Get all posts with hashtag within specified days</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Search Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                {tiktokSearchType.includes('hashtag') ? 'Hashtag (without #)' : 'Search Term'}
              </label>
              <input
                type="text"
                value={tiktokSearchKeyword}
                onChange={(e) => setTiktokSearchKeyword(e.target.value)}
                className="input w-full"
                placeholder={tiktokSearchType.includes('hashtag') ? 'e.g., magic' : 'Enter keyword or competitor name'}
                disabled={tiktokSearching}
              />
            </div>

            {/* Search Parameters */}
            <div className="grid grid-cols-2 gap-4">
              {/* Period/Days parameter - shown for different search types */}
              {(tiktokSearchType === 'keyword' || tiktokSearchType === 'full-keyword') ? (
                <div>
                  <label className="block text-sm font-medium mb-1">Period</label>
                  <select
                    value={tiktokSearchParams.period}
                    onChange={(e) => setTiktokSearchParams((prev: any) => ({ ...prev, period: e.target.value }))}
                    className="input w-full"
                    disabled={tiktokSearching}
                  >
                    <option value="0">Today</option>
                    <option value="1">Yesterday</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="180">Last 180 days</option>
                  </select>
                </div>
              ) : tiktokSearchType === 'full-hashtag' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Days Filter</label>
                    <input
                      type="number"
                      value={tiktokSearchParams.days || 30}
                      onChange={(e) => setTiktokSearchParams((prev: any) => ({ ...prev, days: parseInt(e.target.value) || 30 }))}
                      className="input w-full"
                      placeholder="e.g., 30"
                      disabled={tiktokSearching}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Max Cursor <span className="text-xs text-gray-500">(최대 10000)</span></label>
                    <input
                      type="number"
                      value={tiktokSearchParams.max_cursor || 10000}
                      onChange={(e) => setTiktokSearchParams((prev: any) => ({
                        ...prev,
                        max_cursor: Math.min(10000, parseInt(e.target.value) || 10000)
                      }))}
                      className="input w-full"
                      placeholder="10000"
                      min="100"
                      max="10000"
                      step="100"
                      disabled={tiktokSearching}
                    />
                  </div>
                </>
              ) : null}

              {/* Sorting - only for keyword searches */}
              {(tiktokSearchType === 'keyword' || tiktokSearchType === 'full-keyword') && (
                <div>
                  <label className="block text-sm font-medium mb-1">Sorting</label>
                  <select
                    value={tiktokSearchParams.sorting}
                    onChange={(e) => setTiktokSearchParams((prev: any) => ({ ...prev, sorting: e.target.value }))}
                    className="input w-full"
                    disabled={tiktokSearching}
                  >
                    <option value="0">By Relevance</option>
                    <option value="1">Most Liked</option>
                  </select>
                </div>
              )}

              {/* Minimum Views - for all types */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Minimum Views <span className="text-xs text-gray-500">(optional)</span>
                </label>
                <input
                  type="number"
                  value={tiktokMinViews || ''}
                  onChange={(e) => setTiktokMinViews(e.target.value ? parseInt(e.target.value) : 0)}
                  className="input w-full"
                  placeholder="Enter minimum views"
                  disabled={tiktokSearching}
                />
              </div>

              {/* Email filter - for all types */}
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={tiktokSearchParams.email_filter}
                    onChange={(e) => setTiktokSearchParams((prev: any) => ({ ...prev, email_filter: e.target.checked }))}
                    disabled={tiktokSearching}
                  />
                  <span className="text-sm">Only creators with emails</span>
                </label>
              </div>
            </div>

            {/* Additional Options */}
            <div className="mt-4">
              {/* Multi-cursor option for hashtag and keyword search */}
              {(tiktokSearchType === 'hashtag' || tiktokSearchType === 'keyword') && (
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={tiktokSearchParams.enable_multi_cursor}
                      onChange={(e) => setTiktokSearchParams((prev: any) => ({ ...prev, enable_multi_cursor: e.target.checked }))}
                      disabled={tiktokSearching}
                    />
                    <span className="text-sm">Multi-cursor search (fetch multiple pages automatically)</span>
                  </label>

                  {tiktokSearchParams.enable_multi_cursor && (
                    <div className="mt-2 ml-6">
                      <label className="block text-sm font-medium mb-1">
                        Max cursor value <span className="text-xs text-gray-500">(최대 5000)</span>
                      </label>
                      <input
                        type="number"
                        value={tiktokSearchParams.multi_cursor_max || 1000}
                        onChange={(e) => setTiktokSearchParams((prev: any) => ({
                          ...prev,
                          multi_cursor_max: Math.min(5000, parseInt(e.target.value) || 1000)
                        }))}
                        className="input w-full"
                        placeholder="1000"
                        min="20"
                        max="5000"
                        step="20"
                        disabled={tiktokSearching}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        약 {Math.floor((tiktokSearchParams.multi_cursor_max || 1000) / 20)}페이지 검색 예상
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Match exactly - only for keyword searches */}
              {(tiktokSearchType === 'keyword' || tiktokSearchType === 'full-keyword') && (
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={tiktokSearchParams.match_exactly}
                    onChange={(e) => setTiktokSearchParams((prev: any) => ({ ...prev, match_exactly: e.target.checked }))}
                    disabled={tiktokSearching}
                  />
                  <span className="text-sm">Match exactly in caption</span>
                </label>
              )}
            </div>

            {/* Search Logs */}
            <SearchProgressLogs
              logs={searchLogs}
              isExpanded={showSearchLogs}
              onToggle={() => setShowSearchLogs(!showSearchLogs)}
              creatorFilterStats={tiktokSearchResult?.creatorFilterStats}
              searchType={tiktokSearchType}
              keyword={tiktokSearchKeyword}
            />

            {/* Search Button */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                className="btn btn-secondary"
                onClick={closeModal}
                disabled={tiktokSearching}
              >
                Cancel
              </button>
              
              {tiktokSearching ? (
                <button className="btn btn-primary" disabled>
                  <span className="animate-spin inline-block mr-2">⏳</span>
                  Searching...
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={searchTikTokCreators}
                  disabled={!tiktokSearchKeyword.trim()}
                >
                  🔍 {
                    tiktokSearchType === 'hashtag' ? 'Search Hashtag' :
                    tiktokSearchType === 'full-hashtag' ? 'Search Full Hashtag' :
                    tiktokSearchType === 'full-keyword' ? 'Search Full Keyword' :
                    'Search Keyword'
                  }
                </button>
              )}
            </div>
          </>
        ) : (
          /* Search Results UI */
          <>
            {/* Search Logs at the top of results */}
            {searchLogs.length > 0 && (
              <SearchProgressLogs
                logs={searchLogs}
                isExpanded={showSearchLogs}
                onToggle={() => setShowSearchLogs(!showSearchLogs)}
                maxHeight="150px"
                creatorFilterStats={tiktokSearchResult?.creatorFilterStats}
                searchType={tiktokSearchType}
                keyword={tiktokSearchKeyword}
              />
            )}

            {/* Filter Controls */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Sort By</label>
                  <select
                    value={tiktokSortBy}
                    onChange={(e) => setTiktokSortBy(e.target.value as any)}
                    className="input w-full"
                  >
                    <option value="views">Views</option>
                    <option value="likes">Likes</option>
                    <option value="comments">Comments</option>
                    <option value="date">Date</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Min Views</label>
                  <input
                    type="number"
                    value={tiktokMinViews}
                    onChange={(e) => setTiktokMinViews(parseInt(e.target.value) || 0)}
                    className="input w-full"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Results Statistics */}
            {(() => {
              // For full-hashtag search, get top post per creator
              const filtered = tiktokSearchType === 'full-hashtag'
                ? getTopPostsPerCreator(tiktokSearchResult.data || [])
                : sortAndFilterTikTokResults(tiktokSearchResult.data || []);
              const totalViews = filtered.reduce((sum: number, post: any) => sum + (post.videoMeta?.playCount || post.playCount || post.statistics?.play_count || 0), 0);
              const avgViews = filtered.length > 0 ? Math.round(totalViews / filtered.length) : 0;

              return (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="text-sm">
                      <strong>{filtered.length}</strong> posts found
                      {tiktokSearchType !== 'full-hashtag' && tiktokSearchResult.data && filtered.length < tiktokSearchResult.data.length &&
                        ` (filtered from ${tiktokSearchResult.data.length})`
                      }
                    </div>
                    <button
                      className="btn btn-secondary"
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none'
                      }}
                      onClick={() => setShowHashtagModal(true)}
                    >
                      🔍 Analyze Hashtags
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Select All Checkbox */}
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedPosts.size > 0 &&
                    (tiktokSearchType === 'full-hashtag'
                      ? getTopPostsPerCreator(tiktokSearchResult.data || [])
                      : sortAndFilterTikTokResults(tiktokSearchResult.data || []))
                      .every((post: any) => selectedPosts.has(post.itemInfos?.id || post.aweme_id || post.id))}
                  onChange={toggleSelectAllPosts}
                />
                <span>Select All</span>
              </label>
            </div>

            {/* Search in progress */}
            {tiktokSearching ? (
              <div className="text-center py-8">
                <div className="loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    border: '3px solid #333',
                    borderTop: '3px solid #ff0050',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span>Searching TikTok creators...</span>
                </div>
              </div>
            ) : (tiktokSearchType === 'full-hashtag'
              ? getTopPostsPerCreator(tiktokSearchResult.data || [])
              : sortAndFilterTikTokResults(tiktokSearchResult.data || [])).length === 0 ? (
              /* No results found */
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                <div style={{ fontSize: '2rem', marginBottom: '20px' }}>🎵</div>
                <p>No TikTok posts found for this search.</p>
                {tiktokSearchResult.data && tiktokSearchResult.data.length > 0 && (
                  <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>
                    ({tiktokSearchResult.data.length} posts were filtered out)
                  </p>
                )}
              </div>
            ) : (
              /* Results List - Card Grid Layout like Instagram */
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '16px',
                padding: '16px 0',
                maxHeight: '600px',
                overflowY: 'auto'
              }}>
                {(tiktokSearchType === 'full-hashtag'
                  ? getTopPostsPerCreator(tiktokSearchResult.data || [])
                  : sortAndFilterTikTokResults(tiktokSearchResult.data || [])).map((post: any, index: number) => {
                const creatorName = post.authorInfos?.uniqueId || post.authorMeta?.name || post.author?.unique_id || post.author_meta?.unique_id;
                const isAdded = addedCreators.has(creatorName);
                const isExisting = existingCreators.has(creatorName);
                const isAdding = addingCreators.has(creatorName);

                // Extract email from signature
                const signature = post.authorInfos?.signature || post.authorMeta?.signature || post.author?.signature || '';
                const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
                const emailMatch = signature.match(emailRegex);
                const creatorEmail = emailMatch ? emailMatch[0] : '';
                
                // Format view count
                const formatViews = (count: number): string => {
                  if (!count) return '0';
                  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
                  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
                  return count.toString();
                };
                
                const viewCount = post.itemInfos?.playCount || post.videoMeta?.playCount || post.playCount || post.statistics?.play_count || 0;
                const likeCount = post.itemInfos?.diggCount || post.videoMeta?.diggCount || post.diggCount || post.statistics?.digg_count || 0;
                const commentCount = post.itemInfos?.commentCount || post.videoMeta?.commentCount || post.commentCount || post.statistics?.comment_count || 0;
                const shareCount = post.videoMeta?.shareCount || post.shareCount || post.statistics?.share_count || 0;
                const followerCount = post.authorMeta?.fans || post.author_meta?.followers || post.author?.followers || 0;
                
                return (
                  <div 
                    key={post.id || index} 
                    style={{ 
                      backgroundColor: isExisting ? '#1a2a1a' : '#2a2a2a',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      border: isAdding 
                        ? '2px solid #2563eb' 
                        : selectedPosts.has(post.id)
                          ? '2px solid #667eea'
                          : isExisting 
                            ? '2px solid #51cf66' 
                            : '1px solid #333',
                      position: 'relative',
                      boxShadow: isAdding 
                        ? '0 0 15px rgba(37, 99, 235, 0.5)' 
                        : selectedPosts.has(post.id) 
                          ? '0 0 10px rgba(102, 126, 234, 0.3)'
                          : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!isAdding) {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isAdding) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = isAdding 
                          ? '0 0 15px rgba(37, 99, 235, 0.5)'
                          : selectedPosts.has(post.id)
                            ? '0 0 10px rgba(102, 126, 234, 0.3)'
                            : 'none';
                      }
                    }}
                  >
                    <a 
                      href={`https://www.tiktok.com/@${creatorName}/video/${post.itemInfos?.id || post.aweme_id || post.id || post.video_id}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      {/* Video Thumbnail */}
                      {(post.itemInfos?.covers?.[0] || post.video?.cover?.url_list?.[0] || post.videoMeta?.coverUrl || post.videoMeta?.originalCoverUrl || post.cover || post.video?.cover || post.thumbnail_url || post.video?.thumbnail || post.coverUrl || post.video_info?.cover) ? (
                        <div style={{
                          width: '100%',
                          paddingBottom: '125%', // TikTok videos are usually 9:16
                          position: 'relative',
                          backgroundColor: '#000'
                        }}>
                          <img 
                            src={post.itemInfos?.covers?.[0] || post.video?.cover?.url_list?.[0] || post.videoMeta?.coverUrl || post.videoMeta?.originalCoverUrl || post.cover || post.video?.cover || post.thumbnail_url || post.video?.thumbnail || post.coverUrl || post.video_info?.cover}
                            alt="TikTok Video" 
                            style={{ 
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.style.backgroundColor = '#333';
                            }}
                          />
                          {/* Stats Overlay */}
                          <div style={{
                            position: 'absolute',
                            bottom: '8px',
                            left: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            display: 'flex',
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                              👁 {formatViews(viewCount)}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                              ❤️ {formatViews(likeCount)}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                              💬 {formatViews(commentCount)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          width: '100%',
                          paddingBottom: '125%',
                          backgroundColor: '#333',
                          position: 'relative'
                        }}>
                          <span style={{ 
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#666'
                          }}>No Thumbnail</span>
                        </div>
                      )}
                    </a>
                    
                    {/* Selection Checkbox */}
                    {!isExisting && !isAdded && !isAdding && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        zIndex: 10,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '4px',
                        padding: '4px'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedPosts.has(post.itemInfos?.id || post.aweme_id || post.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            togglePostSelection(post.itemInfos?.id || post.aweme_id || post.id);
                          }}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Creator Info */}
                    <div style={{ padding: '12px' }}>
                      <a 
                        href={`https://www.tiktok.com/@${creatorName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <div style={{ 
                          fontSize: '13px',
                          fontWeight: 'bold',
                          color: '#fff',
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          @{creatorName}
                        </div>
                      </a>
                      
                      {/* Follower Count and Email */}
                      {followerCount > 0 && (
                        <div style={{
                          fontSize: '11px',
                          color: '#aaa',
                          marginBottom: '8px'
                        }}>
                          👥 {formatViews(followerCount)} followers
                        </div>
                      )}
                      
                      {/* Email Display */}
                      {creatorEmail && (
                        <div style={{
                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                          color: '#22c55e',
                          border: '1px solid #22c55e',
                          borderRadius: '4px',
                          padding: '4px 6px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          textAlign: 'center',
                          marginBottom: '8px',
                          wordBreak: 'break-all'
                        }}>
                          📧 {creatorEmail}
                        </div>
                      )}
                      
                      {/* Caption/Description */}
                      {(post.text || post.description) && (
                        <div style={{ 
                          fontSize: '11px',
                          color: '#888',
                          marginBottom: '8px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: '1.4'
                        }}>
                          {post.text || post.description}
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addTikTokCreator(post);
                          }}
                          disabled={isAdding || isAdded || isExisting}
                          style={{
                            flex: 1,
                            padding: '6px 12px',
                            backgroundColor: isExisting ? '#51cf66' : isAdded ? '#868e96' : isAdding ? '#666' : '#ff0050',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: (isAdding || isAdded || isExisting) ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.2s',
                            opacity: isExisting ? 0.8 : isAdded ? 0.6 : isAdding ? 0.7 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!isAdding && !isAdded && !isExisting) {
                              e.currentTarget.style.backgroundColor = '#ff3366';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isAdding && !isAdded && !isExisting) {
                              e.currentTarget.style.backgroundColor = '#ff0050';
                            }
                          }}
                        >
                          {isExisting ? '✓ In DB' : isAdded ? '✓ Added' : isAdding ? '⏳ Adding...' : '➕ Add'}
                        </button>
                        
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            fetchTranscript(`https://www.tiktok.com/@${creatorName}/video/${post.id || post.video_id}`);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#a78bfa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#8b5cf6';
                          }}
                        >
                          📝
                        </button>
                      </div>
                    </div>
                  </div>
                );
                })}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setTiktokSearchResult(null);
                  setSelectedPosts(new Set());
                }}
              >
                ← Back to Search
              </button>
              
              <div className="flex gap-3">
                <button
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
                
                <button
                  className="btn btn-primary"
                  onClick={addSelectedPosts}
                  disabled={selectedPosts.size === 0 || bulkAddingPosts}
                >
                  {bulkAddingPosts ? (
                    <>Adding {selectedPosts.size} creators...</>
                  ) : (
                    <>Add Selected ({selectedPosts.size})</>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </BaseModal>

    {/* Hashtag Analysis Modal */}
    <HashtagAnalysisModal
      isOpen={showHashtagModal}
      onClose={() => setShowHashtagModal(false)}
      posts={tiktokSearchType === 'full-hashtag'
        ? getTopPostsPerCreator(tiktokSearchResult?.data || [])
        : sortAndFilterTikTokResults(tiktokSearchResult?.data || [])}
    />
    </>
  );
};

export default TikTokCreatorsSearchModal;